import { Eye, EyeOff } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { toast } from 'sonner'

import { Button } from '@/components/button'
import { ErrorField } from '@/components/error-field'
import * as Input from '@/components/input'
import { Load } from '@/components/load'
import { RegisterSponsorFormValues } from '@/schemas/auth-schema'
import api from '@/services/api'
import { normalizeCnpj, normalizeCpf, normalizePhone } from '@/utils/mask'

import { ModalTerms } from '../modal-terms/modal-terms'
import { screenTextSponsor } from './forms'

interface SponsorFormProps {
  handleUpdateValueTermsModal: (value: boolean) => void
}

export function SponsorForm({ handleUpdateValueTermsModal }: SponsorFormProps) {
  const { handleSubmit, setValue, register, formState, watch } =
    useFormContext<RegisterSponsorFormValues>()
  const [showPassword, setShowPassword] = useState(false)
  const [terms, setTerms] = useState(false)

  const registrationType = watch('tp_cadastro')
  const nationality = watch('nacionalidade')
  const document = watch('documento')
  const phone = watch('phone')

  const isNotBrazilian = nationality === 'Estrangeiro'
  const isLegal = registrationType === 'P'
  const isPhysical = registrationType === 'F'

  const { textSponsor } = screenTextSponsor(isNotBrazilian, isPhysical, isLegal)

  useEffect(() => {
    if (document === '' || document === undefined || isNotBrazilian) return
    setValue(
      'documento',
      isPhysical ? normalizeCpf(document) : normalizeCnpj(document),
    )
  }, [document, isNotBrazilian, isPhysical, setValue])

  useEffect(() => {
    if (phone === '' || phone === undefined) return
    setValue('phone', normalizePhone(phone))
  }, [phone, setValue])

  function showAndHidePassword() {
    setShowPassword(!showPassword)
  }

  async function handleRegister(data: RegisterSponsorFormValues) {
    const formData = { ...data, termos_aceitos: terms }
    await api
      .post('/user', formData)
      .then(() => handleUpdateValueTermsModal(true))
      .catch((error) =>
        toast.error(error.response.data.message, {
          className: 'register-sponsor-error',
        }),
      )
  }

  return (
    <form onSubmit={handleSubmit(handleRegister)} className="space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            onClick={() => setValue('nacionalidade', 'Brasileiro')}
            variant={nationality === 'Brasileiro' ? 'primary' : 'ghost'}
            data-testid="register-sponsor-brazilian-button"
          >
            Brasileiro
          </Button>
          <Button
            type="button"
            onClick={() => setValue('nacionalidade', 'Estrangeiro')}
            variant={nationality === 'Estrangeiro' ? 'primary' : 'ghost'}
            data-testid="register-sponsor-foreigner-button"
          >
            Estrangeiro
          </Button>
        </div>
      </div>

      <div>
        <Input.Label mandatory className="text-2xl">
          {textSponsor.registerType}
        </Input.Label>
        <div className="flex gap-10">
          <div className="flex items-center gap-2">
            <input
              type="radio"
              id="physical"
              value="F"
              data-testid="register-sponsor-type-physical"
              {...register('tp_cadastro')}
            />
            <label htmlFor="physical">{textSponsor.physical}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="radio"
              id="legal"
              value="P"
              data-testid="register-sponsor-type-legal"
              {...register('tp_cadastro')}
            />
            <label htmlFor="legal">{textSponsor.legal}</label>
          </div>
        </div>
      </div>

      <div>
        <Input.Label htmlFor="name" mandatory>
          {textSponsor.form.name.label}
        </Input.Label>
        <Input.Root withError={!!formState.errors.nome}>
          <Input.Control
            id="name"
            type="text"
            placeholder={textSponsor.form.name.placeholder}
            data-testid="register-sponsor-name-input"
            {...register('nome')}
          />
        </Input.Root>
        {formState.errors.nome && (
          <ErrorField>{formState.errors.nome.message}</ErrorField>
        )}
      </div>

      <div>
        <Input.Label htmlFor="phone" mandatory>
          {textSponsor.form.phone.label}
        </Input.Label>
        <Input.Root withError={!!formState.errors.phone}>
          <Input.Control
            id="phone"
            type="tel"
            maxLength={15}
            placeholder={textSponsor.form.phone.placeholder}
            data-testid="register-sponsor-phone-input"
            {...register('phone')}
          />
        </Input.Root>
        {formState.errors.phone && (
          <ErrorField>{formState.errors.phone.message}</ErrorField>
        )}
      </div>

      <div>
        <Input.Label htmlFor="document" mandatory>
          {textSponsor.form.document.label}
        </Input.Label>
        <Input.Root withError={!!formState.errors.documento}>
          <Input.Control
            id="document"
            type="text"
            maxLength={isNotBrazilian ? undefined : isPhysical ? 14 : 18}
            placeholder={textSponsor.form.document.placeholder}
            data-testid="register-sponsor-document-input"
            {...register('documento')}
          />
        </Input.Root>
        {formState.errors.documento && (
          <ErrorField>{formState.errors.documento.message}</ErrorField>
        )}
      </div>

      <div>
        <Input.Label htmlFor="email" mandatory>
          {textSponsor.form.email.label}
        </Input.Label>
        <Input.Root withError={!!formState.errors.email}>
          <Input.Control
            id="email"
            type="text"
            placeholder={textSponsor.form.email.placeholder}
            data-testid="register-sponsor-email-input"
            {...register('email')}
          />
        </Input.Root>
        {formState.errors.email && (
          <ErrorField>{formState.errors.email.message}</ErrorField>
        )}
      </div>

      <div>
        <Input.Label htmlFor="password" mandatory>
          {textSponsor.form.password.label}
        </Input.Label>
        <Input.Root withError={!!formState.errors.password}>
          <Input.Control
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder={textSponsor.form.password.placeholder}
            data-testid="register-sponsor-password-input"
            {...register('password')}
          />
          <Input.Prefix
            onClick={showAndHidePassword}
            className="cursor-pointer text-zinc-500"
            data-testid="register-sponsor-password-toggle"
          >
            {showPassword ? <Eye /> : <EyeOff />}
          </Input.Prefix>
        </Input.Root>
        {formState.errors.password && (
          <ErrorField>{formState.errors.password.message}</ErrorField>
        )}
      </div>

      <div>
        <Input.Label htmlFor="confirmPassword" mandatory>
          {textSponsor.form.confirmPassword.label}
        </Input.Label>
        <Input.Root withError={!!formState.errors.password}>
          <Input.Control
            id="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            placeholder={textSponsor.form.confirmPassword.placeholder}
            data-testid="register-sponsor-confirm-password-input"
            {...register('confirmPassword')}
          />
          <Input.Prefix
            onClick={showAndHidePassword}
            className="cursor-pointer text-zinc-500"
            data-testid="register-sponsor-confirm-password-toggle"
          >
            {showPassword ? <Eye /> : <EyeOff />}
          </Input.Prefix>
        </Input.Root>
        {formState.errors.confirmPassword && (
          <ErrorField>{formState.errors.confirmPassword.message}</ErrorField>
        )}
      </div>

      <Button
        className="w-full"
        type="submit"
        disabled={formState.isSubmitting || terms === false}
        data-testid="register-sponsor-submit-button"
      >
        {formState.isSubmitting ? <Load /> : textSponsor.register}
      </Button>

      <div className="flex items-center gap-4">
        <input
          type="checkbox"
          checked={terms}
          onChange={() => setTerms((prev) => !prev)}
          data-testid="register-sponsor-terms-checkbox"
        />
        <p className="text-xs text-monochromatic-500">
          Declaro que li e concordo com os
          <ModalTerms setAccepted={() => setTerms(true)} />
          da plataforma e condições dos
          <span className="ml-1 text-bios-primary">
            Contratos para prestação de Serviços Ambientais.
          </span>
        </p>
      </div>
    </form>
  )
}
